/**
 * PartyDuplicateWarning
 *
 * Yellow warning banner showing possible duplicate entities with options
 * to link to an existing entity or create a new one.
 * Also renders the blue "currently linked entity" info banner.
 */

import type { Party } from '@/gen/proto';

import { PARTY_ROLES } from '../../../types/billing';
import { partyTypeToRoles } from '../../../utils/billing-helpers';
import { Button } from '../../ui';

import type { Entity } from '../../../services/entity-service';


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PartyDuplicateWarningProps {
  isDuplicateWarningVisible: boolean;
  duplicateEntities: Entity[];
  duplicateEntitiesParties: Map<string, Party[]>;
  selectedEntity: Entity | null;
  onLinkToEntity: (entity: Entity) => void;
  onCreateNew: () => void;
  onUnlink: () => void;
}

// ---------------------------------------------------------------------------
// Role label helper
// ---------------------------------------------------------------------------

function roleToLabel(role: string): string {
  const found = PARTY_ROLES.find((r: { value: string; label: string }) => r.value === role);
  return found?.label ?? role;
}

// ---------------------------------------------------------------------------
// DuplicateEntityRow
// ---------------------------------------------------------------------------

interface DuplicateEntityRowProps {
  entity: Entity;
  existingParties: Party[];
  onLink: () => void;
}

function DuplicateEntityRow({ entity, existingParties, onLink }: DuplicateEntityRowProps): JSX.Element {
  return (
    <div className="flex items-center justify-between el-p-sm bg-card rounded border border-yellow-200 dark:border-yellow-700">
      <div className="flex-1">
        <div className="font-medium text-foreground">{entity.name}</div>
        <div className="text-sm text-foreground-secondary">
          {entity.businessIdentifiers
            ?.map(b => `${b.type.toUpperCase()}: ${b.value}`)
            .join(', ')}
        </div>
        {existingParties.length > 0 && (
          <div className="text-sm text-foreground-secondary mt-1">
            <span className="font-medium">Existing accounts: </span>
            {existingParties.map((party, idx) => {
              const roles = (party.roles && party.roles.length > 0)
                ? party.roles
                : partyTypeToRoles(party.partyType);
              const typeLabel = roles.map(roleToLabel).join(', ') || 'Unknown';
              return (
                <span key={party.id}>
                  {idx > 0 && ', '}
                  {typeLabel} ({party.code})
                </span>
              );
            })}
          </div>
        )}
      </div>
      <Button className="ml-3" size="sm" variant="secondary" onClick={onLink}>
        Link to This
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface DuplicateListProps {
  duplicateEntities: PartyDuplicateWarningProps['duplicateEntities']
  duplicateEntitiesParties: PartyDuplicateWarningProps['duplicateEntitiesParties']
  onLinkToEntity: PartyDuplicateWarningProps['onLinkToEntity']
  onCreateNew: () => void
}

function DuplicateEntitiesBox({ duplicateEntities, duplicateEntitiesParties, onLinkToEntity, onCreateNew }: DuplicateListProps): JSX.Element {
  return (
    <div className="el-p-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
      <div className="flex items-start el-gap-md">
        <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        <div className="flex-1">
          <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">Possible Duplicate Entities Found</h4>
          <p className="text-sm text-yellow-700 dark:text-yellow-400 mb-3">
            Found {duplicateEntities.length} existing{' '}
            {duplicateEntities.length === 1 ? 'entity' : 'entities'} with similar identifiers.
            Would you like to link to an existing entity instead?
          </p>
          <div className="el-space-y-sm">
            {duplicateEntities.map(entity => (
              <DuplicateEntityRow
                key={entity.id}
                entity={entity}
                existingParties={duplicateEntitiesParties.get(entity.id) ?? []}
                onLink={() => onLinkToEntity(entity)}
              />
            ))}
          </div>
          <div className="mt-3">
            <Button size="sm" variant="ghost" onClick={onCreateNew}>
              Create New Entity Anyway
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function SelectedEntityBox({ selectedEntity, onUnlink }: { selectedEntity: NonNullable<PartyDuplicateWarningProps['selectedEntity']>; onUnlink: () => void }): JSX.Element {
  return (
    <div className="el-p-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-medium text-blue-900 dark:text-blue-300">Linking to Existing Entity</div>
          <div className="text-sm text-blue-700 dark:text-blue-400 mt-1">{selectedEntity.name}</div>
        </div>
        <Button size="sm" variant="ghost" onClick={onUnlink}>Unlink</Button>
      </div>
    </div>
  )
}

export function PartyDuplicateWarning({
  isDuplicateWarningVisible,
  duplicateEntities,
  duplicateEntitiesParties,
  selectedEntity,
  onLinkToEntity,
  onCreateNew,
  onUnlink,
}: PartyDuplicateWarningProps): JSX.Element {
  return (
    <>
      {isDuplicateWarningVisible && duplicateEntities.length > 0 && (
        <DuplicateEntitiesBox
          duplicateEntities={duplicateEntities}
          duplicateEntitiesParties={duplicateEntitiesParties}
          onCreateNew={onCreateNew}
          onLinkToEntity={onLinkToEntity}
        />
      )}
      {selectedEntity && <SelectedEntityBox selectedEntity={selectedEntity} onUnlink={onUnlink} />}
    </>
  );
}
